import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { AddressService } from '../../services/address.service';
import { CompanyService } from '../../services/company.service';
import { UserService } from '../../services/user.service';
import { Store } from 'src/app/store';
import { Router } from '@angular/router';

@Component({
    selector: 'app-user',
    templateUrl: './user.component.html',
    styleUrls: ['./user.component.scss'],
})
export class UserComponent implements OnInit {
    @Input() user;
    @Input() company;
    @Input() address;
    @Input() newRecord;
    @Input() entryType = 'account';
    @Output() next = new EventEmitter();
    @Output() userCreated = new EventEmitter();

    form: FormGroup;

    constructor(
        private fb: FormBuilder,
        private userService: UserService,
        private addressService: AddressService,
        private companyService: CompanyService,
        private store: Store,
        private router: Router,
    ) {
        this.form = this.fb.group({
            firstname: ['', [Validators.required, Validators.minLength(2)]],
            surname: ['', [Validators.required, Validators.minLength(2)]],
            email: ['', [Validators.required, Validators.email]],
            emailConfirm: ['', [Validators.email]],
            mobile: [''],
            landline: [
                '',
                [
                    Validators.pattern(
                        '^(((\\+44\\s?\\d{4}|\\(?0\\d{4}\\)?)\\s?\\d{3}\\s?\\d{3})|((\\+44\\s?\\d{3}|\\(?0\\d{3}\\)?)\\s?\\d{3}\\s?\\d{4})|((\\+44\\s?\\d{2}|\\(?0\\d{2}\\)?)\\s?\\d{4}\\s?\\d{4}))(\\s?\\#(\\d{4}|\\d{3}))?$'
                    ),
                ],
            ],
            jobTitle: ['', [Validators.minLength(2)]],
        });
    }

    ngOnInit(): void {
        this.form.patchValue({
            firstname: this.user.contact.firstname,
            surname: this.user.contact.surname,
            email: this.user.contact.email,
            jobTitle: this.user.contact.jobTitle,
            mobile: this.user.contact.mobile,
            landline: this.user.contact.landline,
        });
    }

    toggleValue(v) {
        if (!this.user[v]) {
            this.user[v] = true;
        } else {
            this.user[v] = false;
        }
    }

    async onSubmit() {
        this.user.username = this.form.value.email;

        this.user.contact.firstname = this.form.value.firstname;
        this.user.contact.surname = this.form.value.surname;
        this.user.contact.email = this.form.value.email;
        this.user.contact.mobile = this.form.value.mobile?.e164Number ?? '';
        this.user.contact.mobDialCode = this.form.value.mobile?.dialCode ?? '';
        this.user.contact.landline = this.form.value.landline;
        this.user.contact.avatar = this.form.value.avatar;
        this.user.contact.jobTitle = this.form.value.jobTitle;

        if (this.newRecord) {
            // create the user
            const userResult = await this.createUser();
            this.user = userResult;

            // create the address
            this.address.userId = this.user.id;
            const addressResult = await this.createAddress();
            this.address = addressResult;
            this.user.contact.addressId = this.address.id;

            // create the company
            this.company.primaryUserId = this.user.id;
            this.company.addressId = this.address.id;
            const companyResult = await this.createCompany();
            this.user.company = companyResult;
            this.company = companyResult;
            this.user.contact.companyId = this.company.id;

            // tidy up and move on
            this.userCreated.emit({
                user: this.user,
                company: this.company,
                address: this.address,
            });
            if (this.entryType === 'jobLot') {
                const jobLotParams = this.store.selectForLocal('jobLotParams');
                jobLotParams.supplierId = this.company.id;
                this.store.set('jobLotParams', jobLotParams);
                this.router.navigate(['/', 'products', 'create-entry'])
            } else {
                this.next.emit();
            }
        } else {
            await this.updateUser();
            await this.updateCompany();
            await this.updateAddress();

            this.next.emit();
        }
    }

    async createUser() {
        return await this.userService.createUserNew(this.user);
    }

    async createAddress() {
        return await this.userService.createAddressNew(this.address, this.user);
    }

    async createCompany() {
        return await this.userService.createCompanyNew(this.company, this.user);
    }

    async updateUser() {
        this.userService.update(this.user).subscribe((data) => {
            return this.user.id;
        });
    }

    async updateCompany() {
        this.companyService.update(this.company).subscribe((data) => {
            return this.user.id;
        });
    }

    async updateAddress() {
        this.addressService.update(this.address).subscribe((data) => {
            return this.user.id;
        });
    }
}
